/**
 * interactionState.js
 *
 * Phase 6B — Interaction State. The layer between a Saved
 * VisualDefinition's immutable query and what actually gets executed
 * for one render. Per the Phase 5B architectural decision (approved
 * before any Phase 6 work began): cross-filtering / drill-down / Top N
 * NEVER mutate a saved VisualDefinition's query — they are ephemeral,
 * applied only at execution time.
 *
 *   Saved Query (immutable unless the user explicitly saves a change)
 *        ↓
 *   drillDown + crossFilters   →  effectiveQuery  →  execute()  →  full result
 *        ↓
 *   topN (applied AFTER execution, on the result — never part of the query)
 *        ↓
 *   Visual
 *
 * drillDown and crossFilters affect the QUERY (different dimension/
 * filters means the Adapter returns different rows). topN does NOT —
 * it slices an already-complete result. This distinction is
 * architectural, not stylistic: topN never reaches executeQuery()/
 * executeTimeSeriesQuery()/executeMonthlyActualCostQuery() at all.
 */

// ─── Drill-down Hierarchy (Phase 6 Design Gate, approved) ──────────────────
// Confirmed against analyticalQuery.js's actual DATASETS registry, not
// assumed. Only dimension pairs verified to exist and make analytical
// sense are listed — a Visual must never invent a transition not present
// here.
//
//   package: no valid transition — package IS cost_code (a synonym,
//     per the Baseline Doc's "Package = Cost Code description"
//     convention), not a parent of it.
//   cost_code: no valid transition — sub_code is not supported by any
//     Adapter yet ("not yet validated for time-series use" — see
//     timeSeriesSSOTAdapter.js).
//   historical_comparison: no transitions at all — its only dimension
//     is cost_code itself (resource_type/indirect_category were
//     implemented and then withdrawn — see historicalSSOTAdapter.js's
//     file header for the full Direct/Indirect discriminator
//     investigation).
export const DRILL_DOWN_HIERARCHY = {
  cost_performance: {
    cost_type: ['cost_code'],
    resource_type: ['cost_code'],
    indirect_category: ['cost_code'],
  },
  time_series: {
    resource_type: ['cost_code'],
    indirect_category: ['cost_code'],
  },
  monthly_actual_cost: {
    resource_type: ['cost_code'],
    indirect_category: ['cost_code'],
  },
  historical_comparison: {},
}

/**
 * Validates a proposed drill-down transition against the approved
 * hierarchy. Throws rather than silently allowing an unlisted
 * transition — a Visual is never permitted to invent one.
 */
export function validateDrillDown(dataset, fromDimension, toDimension) {
  const allowed = DRILL_DOWN_HIERARCHY[dataset]?.[fromDimension] || []
  if (!allowed.includes(toDimension)) {
    throw new Error(
      `Drill-down from "${fromDimension}" to "${toDimension}" is not ` +
      `permitted for dataset "${dataset}" — allowed transitions: ` +
      `${JSON.stringify(DRILL_DOWN_HIERARCHY[dataset] || {})}`
    )
  }
}

// ─── InteractionState shape (documentation, not a runtime type) ───────────
// InteractionState {
//   drillDown?:    { dimension: string, filter: { [key]: value } }
//   crossFilters?: { [filterKey]: value }
//   topN?:         { mode: 'top' | 'bottom', n: number | 'all', by: string }
// }

/**
 * Builds the query actually sent to an Adapter for one render — the
 * ONLY place drillDown/crossFilters are merged into a query. Never
 * mutates savedDefinition.query itself; always returns a new object.
 *
 * drillDown changes BOTH dimension and filters (moving to a deeper
 * grain, scoped to what was clicked). crossFilters only adds filters
 * (the dimension stays whatever the Saved Query or drillDown already
 * set). If both are present, drillDown's filter and crossFilters are
 * merged together — a later key from crossFilters would overwrite an
 * identical key from drillDown, but in practice drillDown's filter key
 * (e.g. resource_type) and typical crossFilters keys are expected not to
 * collide within one dataset's DRILL_DOWN_HIERARCHY entries.
 */
export function buildEffectiveQuery(savedDefinition, interactionState = {}) {
  const { drillDown, crossFilters } = interactionState
  let query = savedDefinition.query

  if (drillDown) {
    validateDrillDown(query.dataset, query.dimension, drillDown.dimension)
    query = {
      ...query,
      dimension: drillDown.dimension,
      filters: { ...query.filters, ...drillDown.filter },
    }
  }

  if (crossFilters) {
    query = {
      ...query,
      filters: { ...query.filters, ...crossFilters },
    }
  }

  return query
}

/**
 * Applies Top N to an already-executed result — never part of the
 * query, never re-executes anything. Operates on QueryResult.rows
 * (single-period results) or TimeSeriesResult.series[].rows (each time
 * point independently) — same rule applied per point for a time series,
 * not once globally, so "Top 10 Cost Codes by actual" means "the top 10
 * for THIS point" at every point in the series, not one fixed set of 10
 * carried across all of them.
 *
 * by MUST be a measure actually present in the result's values — never
 * falls back to label/key/an unrelated measure. A caller requesting
 * by: 'foo' when 'foo' isn't in the row's values gets an explicit error,
 * not a silently wrong sort.
 */
export function applyTopN(result, topN) {
  if (!topN || topN.n === 'all') return result

  const { mode, n, by } = topN
  if (mode !== 'top' && mode !== 'bottom') {
    throw new Error(`applyTopN: mode must be 'top' or 'bottom', got "${mode}"`)
  }
  if (typeof n !== 'number' || n <= 0) {
    throw new Error(`applyTopN: n must be a positive number or 'all', got ${JSON.stringify(n)}`)
  }
  if (!by || typeof by !== 'string') {
    throw new Error(`applyTopN: "by" must be a measure key string, got ${JSON.stringify(by)}`)
  }

  function sliceRows(rows) {
    // Validate `by` against THIS row set's actual values — not just
    // trusting the caller — before sorting. A row missing the key
    // entirely (not 0, genuinely absent) means `by` doesn't apply to
    // this result shape at all.
    if (rows.length > 0 && !(by in rows[0].values)) {
      throw new Error(
        `applyTopN: measure "${by}" is not present in this result's ` +
        `values (available: ${Object.keys(rows[0].values).join(', ')}) — ` +
        `refusing to fall back to another measure.`
      )
    }
    const sorted = [...rows].sort((a, b) => {
      const diff = (a.values[by] ?? 0) - (b.values[by] ?? 0)
      return mode === 'top' ? -diff : diff
    })
    return sorted.slice(0, n)
  }

  // QueryResult (single period: has `.rows` directly)
  if (result.rows) {
    return { ...result, rows: sliceRows(result.rows) }
  }

  // TimeSeriesResult (has `.series`, each point sliced independently)
  if (result.series) {
    return {
      ...result,
      series: result.series.map(point => ({ ...point, rows: sliceRows(point.rows) })),
    }
  }

  throw new Error('applyTopN: result must have either .rows (QueryResult) or .series (TimeSeriesResult)')
}
